import React, { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import Button from "components/Button";

import { ReactComponent as CloseIcon } from "assets/images/icon_close_modal.svg";

import { useAppDispatch } from "store/hooks";
import { deleteUser } from "store/features/userSlice";
import { clearSession } from "store/features/authSlice";

import Step1 from "./Step1";
import Step2 from "./Step2";

import { routes } from "routes/routes";

import {
  FormValues,
  reasonLabelToEnum,
  validationSchema,
} from "./Step2/validation";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

import { styles } from "./styles";

export enum DeleteModalSteps {
  Step1 = "Step1",
  Step2 = "Step2",
}

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = (props) => {
  const dispatch = useAppDispatch();
  const { isOpen, onClose } = props;

  const [step, setStep] = useState<DeleteModalSteps>(DeleteModalSteps.Step1);
  const [loading, setLoading] = useState(false);

  const methods = useForm<FormValues>({
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
    defaultValues: {
      reason: [],
      notes: "",
    },
  });

  const onCloseModal = () => {
    onClose();
    methods.reset();
    setStep(DeleteModalSteps.Step1);
  };

  const onDeleteAccount = async () => {
    if (step === DeleteModalSteps.Step1) {
      setStep(DeleteModalSteps.Step2);
      return;
    }

    methods.handleSubmit(async (data) => {
      analytics().sendEvent(AnalyticsEvents.profile_delete_account_confirm);
      const { reason, notes } = data;

      const reasonEnum = reason.map((item) => {
        return reasonLabelToEnum[item];
      });

      try {
        setLoading(true);
        await dispatch(deleteUser({ reason: reasonEnum, notes })).unwrap();

        onCloseModal();
        dispatch(clearSession(routes.auth)).unwrap();
      } catch (error) {
        setLoading(false);
      } finally {
        setLoading(false);
      }
    })();
  };

  const renderTitle = () => {
    switch (step) {
      case DeleteModalSteps.Step1:
        return (
          <>Ви впевнені, що хочете назавжди видалити свій обліковий запис?</>
        );
      case DeleteModalSteps.Step2:
        return <>Нам прикро, що ви йдете. Сподіваємося, ви ще повернетеся!</>;
      default:
        return (
          <>Ви впевнені, що хочете назавжди видалити свій обліковий запис?</>
        );
    }
  };

  const renderStep = () => {
    switch (step) {
      case DeleteModalSteps.Step1:
        return <Step1 />;
      case DeleteModalSteps.Step2:
        return <Step2 methods={methods} />;
      default:
        return <Step1 />;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseModal}
      slotProps={{ paper: { sx: styles.modal } }}
    >
      <DialogTitle component="div" sx={{ p: 0, mr: 4 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {renderTitle()}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close-modal"
        onClick={onCloseModal}
        sx={styles.closeButton}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: 0, mr: 2 }}>{renderStep()}</DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            width: "100%",
            gap: 2,
          }}
        >
          <Button
            color="secondary"
            variant="contained"
            onClick={onCloseModal}
            disabled={loading}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Скасувати
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={onDeleteAccount}
            withBorder
            fullWidth
            loading={loading}
          >
            Видалити остаточно
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAccountModal;
